<template>
  <a
    href=""
    class="relative bg-gray-100 inline-block lg:rounded-2xl p-6 2xl:w-45 2xl:h-60 xl:w-35 xl:h-48 lg:w-28 lg:h-40 w-18 h-26 rounded-lg shadow-lg overflow-hidden ml-0 transform transition-all duration-200"
    :class="[isActive ? '' : 'opacity-50 scale-95 translate-y-1', animateHover && isActive ? 'hover:scale-105 hover:translate-y-0' : '', isDragging ? 'opacity-50 scale-95 z-50' : '']"
    @click.prevent="emit('click')"
    :style="{ cursor: cursor }"
    :draggable="isDraggable && isActive"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
  >
    <!-- Corner decorations -->
    <div class="absolute top-0 right-0 w-12 h-12 lg:w-14 lg:h-14 2xl:w-16 2xl:h-16 transform rotate-45 translate-x-8 -translate-y-8" :class="cornerColor"></div>
    <div class="absolute bottom-0 left-0 w-12 h-12 lg:w-14 lg:h-14 2xl:w-16 2xl:h-16 transform rotate-45 -translate-x-8 translate-y-8" :class="cornerColor"></div>

    <!-- Top left content -->
    <div v-if="showRank" class="absolute top-1 -left-3 md:-left-2 2xl:left-2 xl:-left-1 flex flex-col items-center justify-center">
      <div class="text-black md:text-xs lg:text-sm xl:text-xl 2xl:text-2xl 2xl:ml-[2px] font-bold">
        {{ trimString(card?.rank || '') }}
      </div>
      <div v-if="showLetter" class="relative w-12 h-12 flex justify-center md:-mt-4 lg:-mt-2 2xl:mt-0 -mt-5">
        <DiamondImage v-if="card?.suit == 'diamonds'" class="2xl:w-full xl:w-9 lg:w-8 md:w-5 h-full w-4 object-contain text-cw-red" />
        <HeartImage v-else-if="card?.suit == 'hearts'" class="2xl:w-full xl:w-9 lg:w-8 md:w-5 h-full w-4 object-contain text-cw-red" />
        <ClubImage v-else-if="card?.suit == 'clubs'" class="2xl:w-full xl:w-9 lg:w-8 md:w-5 h-full w-4 object-contain text-black" />
        <SpadeImage v-else-if="card?.suit == 'spades'" class="2xl:w-full xl:w-9 lg:w-8 md:w-5 h-full w-4 object-contain text-black" />
        <span class="absolute inset-0 flex items-center justify-center text-white text-[6px] sm:text-[8px] lg:text-xs xl:text-sm 2xl:text-lg font-bold z-10">
          {{ trimString(card?.letter || '') }}
        </span>
      </div>
    </div>

    <!-- Center content -->
    <div class="flex flex-col items-center justify-center h-full text-center">
      <h1 v-if="isLetterCard" class="md:text-base lg:text-xl xl:text-2xl 2xl:text-4xl font-medium mb-4" :class="titleColor">{{ trimString(letter || '').toUpperCase() }}</h1>
      <h1 class="md:text-sm lg:text-lg xl:text-xl 2xl:text-3xl font-bold font-[cursive] text-sm capitalize" :class="titleColor">
        {{ trimString(card?.category || '') }}
      </h1>
      <p class="md:text-[6px] lg:text-[8px] xl:text-xs 2xl:text-xs text-[5px]" :class="subtitleColor">
        {{ trimString(card?.type || '') }}
      </p>
    </div>

    <!-- Bottom right content (rotated) -->
    <div class="absolute flex flex-col items-center bottom-1 -right-3 md:-right-2 xl:-right-1 2xl:right-2 transform rotate-180">
      <div v-if="showRank" class="text-black md:text-xs xl:text-xl 2xl:text-2xl md:mr-6 lg:mr-6 2xl:-mr-[2px] font-bold">
        {{ trimString(card?.rank || '') }}
      </div>
      <div v-if="showLetter" class="relative w-12 h-12 flex items-center justify-center -mt-5 md:mr-6 2xl:mr-0 md:-mt-4 lg:-mt-2 xl:-mt-2 2xl:mt-0">
        <DiamondImage v-if="card?.suit == 'diamonds'" class="2xl:w-full xl:w-9 lg:w-8 md:w-5 w-4 h-full object-contain text-cw-red" />
        <HeartImage v-else-if="card?.suit == 'hearts'" class="2xl:w-full xl:w-9 lg:w-8 md:w-5 w-4 h-full object-contain text-cw-red" />
        <ClubImage v-else-if="card?.suit == 'clubs'" class="2xl:w-full xl:w-9 lg:w-8 md:w-5 w-4 h-full object-contain text-black" />
        <SpadeImage v-else-if="card?.suit == 'spades'" class="2xl:w-full xl:w-9 lg:w-8 md:w-5 w-4 h-full object-contain text-black" />
        <span class="absolute inset-0 transform scale-x-100 flex items-center justify-center text-white text-[6px] md:text-[8px] lg:text-xs xl:text-sm 2xl:text-lg font-bold z-10">
          {{ trimString(card?.letter || '') }}
        </span>
      </div>
    </div>
  </a>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import DiamondImage from '@/assets/svgs/cards/Diamond.svg'
import HeartImage from '@/assets/svgs/cards/Heart.svg'
import ClubImage from '@/assets/svgs/cards/Clubs.svg'
import SpadeImage from '@/assets/svgs/cards/Spade.svg'
import type { CardT, CardThemeT } from '@/types/Game'
import { useHelpers } from '@/composables/useHelpers'

const {
  card,
  isActive = true,
  showRank = true,
  showLetter = true,
  isLetterCard = false,
  animateHover = false,
  cursor = 'default',
  letter = '',
  isDraggable = false
} = defineProps<{
  card: Partial<CardT> | null
  isActive?: boolean
  isLetterCard?: boolean
  animateHover?: boolean
  showRank?: boolean
  showLetter?: boolean
  letter?: string
  cursor?: 'pointer' | 'default' | 'not-allowed' | 'grab' | 'grabbing'
  isDraggable?: boolean
}>()

const emit = defineEmits<{
  click: []
  dragstart: [event: DragEvent, card: Partial<CardT>]
  dragend: []
}>()

const { trimString } = useHelpers()

const isDragging = ref(false)

const handleDragStart = (event: DragEvent) => {
  if (!isDraggable || !isActive || !card) return

  isDragging.value = true

  // Set drag data
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('application/json', JSON.stringify(card))
  }

  emit('dragstart', event, card)
}

const handleDragEnd = () => {
  isDragging.value = false
  emit('dragend')
}

const themeColors: Record<CardThemeT, { corner: string; title: string; subtitle: string }> = {
  plan: {
    corner: 'bg-cw-red-alt',
    title: 'text-cw-red-alt',
    subtitle: 'text-cw-red-alt'
  },
  invest: {
    corner: 'bg-blue-600',
    title: 'text-blue-600',
    subtitle: 'text-blue-600'
  },
  save: {
    corner: 'bg-purple-600',
    title: 'text-purple-600',
    subtitle: 'text-purple-600'
  }
}

// Computed properties with null safety
const cornerColor = computed(() => {
  if (!card || !card.category) return 'bg-gray-400'
  return themeColors[card.category]?.corner || 'bg-gray-400'
})

const titleColor = computed(() => {
  if (!card || !card.category) return 'text-gray-600'
  return themeColors[card.category]?.title || 'text-gray-600'
})

const subtitleColor = computed(() => {
  if (!card || !card.category) return 'text-gray-500'
  return themeColors[card.category]?.subtitle || 'text-gray-500'
})
</script>

<style scoped>
@reference "#tailwind";

:deep(svg.text-black path) {
  @apply text-black;
}

:deep(svg.text-cw-red path) {
  @apply text-cw-red;
}
</style>
